// (2026-05-13) Req 2: formatMoney unifica formatMoney, formatMoneySpecial y
// formatMoneyByMoneda en una sola firma.
//
// Firma:
//   formatMoney(value, moneda = null, digits = 2)
//
// Comportamiento:
//   - Si `moneda` es un objeto { codigo, simbolo, posicion }, lo usa para el
//     render (simbolo antes o despues segun `posicion`).
//   - Si `moneda` es un numero, se trata como `digits` (compat retro con la
//     firma legacy `formatMoneySpecial(value, digits)`).
//   - Si `moneda` no se provee (null/undefined), cae al formato VES por defecto.
//
// Ejemplos:
//   formatMoney(100)                                    -> "100,00 Bs"
//   formatMoney(100, 4)                                 -> "100,0000 Bs"   (compat)
//   formatMoney(100, {codigo:'USD', simbolo:'$', posicion:'antes'})    -> "$ 100,00"
//   formatMoney(100, {codigo:'VES', simbolo:'Bs', posicion:'despues'}) -> "100,00 Bs"
//
const DEFAULT_MONEDA_VES = { codigo: 'VES', simbolo: 'Bs', posicion: 'despues' };

// (2026-05-18 A9) Un monto fiscal AUSENTE/inválido NO debe mostrarse como
// "0,00" (falsea un valor real). Inválido = null, undefined, cadena vacía o
// no numérico. El CERO real (0, "0", 0.0) SÍ es válido y se formatea normal.
// Inválido => placeholder "—".
const _NUM_PLACEHOLDER = "—";
const _esMontoInvalido = (v) =>
  v === null || v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  Number.isNaN(Number(v));

export const formatMoney = (value, moneda = null, digits = 2) => {
  // Compat retro: formatMoneySpecial(value, digits) — segundo arg numerico.
  if (typeof moneda === 'number') {
    digits = moneda;
    moneda = null;
  }
  if (_esMontoInvalido(value)) return _NUM_PLACEHOLDER;
  const m = moneda || DEFAULT_MONEDA_VES;
  const num = new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  }).format(Number(value));
  const sim = (m.simbolo || '').trim();
  const pos = ((m.posicion || 'antes') + '').trim();
  return pos === 'despues' ? `${num} ${sim}` : `${sim} ${num}`;
};

// Aliases retrocompatibles. Nuevo codigo debe usar formatMoney directamente.
export const formatMoneySpecial = formatMoney;
export const formatMoneyByMoneda = formatMoney;

// (2026-05-15) Devuelve el SIMBOLO de una moneda a partir de su codigo.
// Usado para etiquetas de graficos/rankings (no aplica posicion).
// Si el codigo no se reconoce, devuelve el valor recibido tal cual
// (tolera que el backend envie ya un simbolo). Vacio -> 'Bs'.
export const monedaSimbolo = (codigo) => {
  if (!codigo) return 'Bs';
  const c = String(codigo).trim().toUpperCase();
  const map = { VES: 'Bs', USD: '$', EUR: '€' };
  return map[c] || codigo;
};

// (2026-05-15) Unificado: formatDecimal acepta `digits` (default 2).
// `formatDecimalSpecial` queda como alias retrocompatible.
export const formatDecimal = (value, digits = 2) => {
  // (2026-05-18 A9) inválido => "—" (no falsear como 0,00); el 0 real sí formatea.
  if (_esMontoInvalido(value)) return _NUM_PLACEHOLDER;
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  }).format(Number(value));
};

export const formatDecimalSpecial = formatDecimal;

export const formatInteger = (value) => {
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(Number(value || 0));
};

/*export const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (isNaN(date.getTime())) return "";

  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};*/

export const formatDate = (value) => {
  if (!value) return "";

  const parts = value.split("-");

  if (parts.length !== 3) return "";

  const [year, month, day] = parts.map(Number);
  const date = new Date(year, month - 1, day);

  if (isNaN(date.getTime())) return "";

  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const formatDateTime = (value, withSeconds = true) => {
  if (!value) return "";

  const date = new Date(value);
  if (isNaN(date.getTime())) return "";

  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...(withSeconds && { second: "2-digit" }),
  }).replace(',', '');
};

export const formatText = (value) => {
  if (value === null || value === undefined) return "";

  return value
    .toString()
    .trim()        // elimina espacios al inicio y al final
    .toUpperCase(); // convierte a mayúsculas
};

export const formatFiscalPeriod = (value) => {
  if (!value) return "";

  // (2026-05-18 A10) Antes: new Date(value). Con 'YYYY-MM' o 'YYYY-MM-DD'
  // el parseo es UTC y en husos negativos (Venezuela UTC-4) podía mostrar
  // el MES ANTERIOR. Se extrae año/mes por patrón, sin Date, para los
  // formatos esperados ('YYYY-MM', 'YYYY-MM-DD', ISO 'YYYY-MM-DDT...').
  const s = String(value).trim();
  const m = s.match(/^(\d{4})[-/](\d{1,2})/);
  if (m) {
    const year = m[1];
    const month = String(parseInt(m[2], 10)).padStart(2, "0");
    return `${year}-${month}`;
  }

  // Fallback solo si no matchea el patrón esperado.
  const date = new Date(s);
  if (isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

export const validateFormatEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
  const isValidEmail = emailRegex.test(email);
  return isValidEmail;
};

export const validateFormatPhone = (phone) => {
  if (!phone) return false;
  const phoneRegex = /^\d{4}-\d{7}$/;
  const isValidPhone = phoneRegex.test(phone);
  return isValidPhone;
};

// (2026-08-03, ronda 2 / T1) La clase incluye 'C' (Comunal). Es el validador
// GENÉRICO (el que se usa cuando NO hay un tipo del Catálogo 7 elegido, y en el
// resto de formularios con RIF), y su gemelo de backend `utils.validar_rif` ya
// admite la letra. Sin este cambio quedaba un hueco real: un RIF comunal válido
// para el servidor lo rechazaba el formulario en la rama "sin tipo".
export const validateFormatRif = (rif) => {
  if (!rif) return false;
  const rifRegex = /^(?:\d{6,8}|[JVEGPC]-\d{8}-\d)$/;
  return rifRegex.test(rif);
};

// =====================================================================
// (2026-08-02, ítems 16 / 18 / 28) CATÁLOGO 7 — helpers COMPARTIDOS por
// FormEndClients (cliente final) y FormClients (empresa).
//
// Son el ESPEJO EXACTO del backend; cada función indica su gemela:
//   contarDigitosDocumento   <- utils.contar_digitos_documento (utils.py:359)
//   esFirmaPersonal          <- 'FIRMA' in descripcion.upper()
//                               (servicios_compartidos.py:268-269, :330)
//   validarLongitudCat7      <- servicios_compartidos.validar_tipo_identificacion
//                               (servicios_compartidos.py:283-349)
//   validarCoherenciaRifCat7 <- servicios_compartidos.
//                               validar_tipo_identificacion_empresa, bloque (3)
//                               (servicios_compartidos.py:403-421)
//   patronRifCat7            <- utils.validar_rif (utils.py:342) aplicado cuando
//                               es_rif=True, y "solo dígitos" cuando es_rif=False
//                               (client_routes.py:359-370)
//
// REGLA DURA heredada del backend: NUNCA se manipula/rellena/recorta el número
// del documento; solo se CUENTA su parte numérica para aceptar o rechazar.
// =====================================================================

// Espejo EXACTO de utils.contar_digitos_documento(valor): cuenta SOLO los
// dígitos (ignora la letra prefijo y los guiones). NO transforma nada.
export const contarDigitosDocumento = (valor) => {
  if (valor === null || valor === undefined) return 0;
  const s = String(valor);
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] >= '0' && s[i] <= '9') n++;
  }
  return n;
};

// Detecta la fila 'V Firma personal' por su descripción (igual que el backend).
export const esFirmaPersonal = (descripcion) =>
  String(descripcion || '').toUpperCase().includes('FIRMA');

// Semántica cerrada por el cliente (ítem 16) y replicada en el fallback del
// backend (`seniat_routes._CAT7_FALLBACK`): SOLO 'V' Persona Natural lleva
// es_rif=false; el resto (incluida la 2ª 'V' de Firma personal) es_rif=true.
// Se usa ÚNICAMENTE cuando el catálogo no publica el flag (backend viejo), y
// en ese caso el llamador debe avisar de la degradación.
export const esRifDerivadoCat7 = (codigo, descripcion) => {
  const c = String(codigo || '').toUpperCase();
  if (c === 'V') return esFirmaPersonal(descripcion);
  return true;
};

// Catálogo 7 por defecto. Espejo fila a fila de `_CAT7_FALLBACK`
// (seniat_routes.py:1163-1176), incluidas las DOS filas 'V'. `id: null` =>
// no hay FK que enviar; el backend resolverá la fila 'V' por longitud
// (camino legacy). Su uso SIEMPRE debe ir acompañado de un aviso visible.
export const CAT7_FALLBACK = [
  { id: null, codigo: 'V', descripcion: 'Natural de la República Bolivariana de Venezuela', es_rif: false },
  { id: null, codigo: 'J', descripcion: 'Persona Jurídica', es_rif: true },
  { id: null, codigo: 'E', descripcion: 'Extranjero con residencia en Venezuela', es_rif: true },
  { id: null, codigo: 'P', descripcion: 'Agente registrado con Pasaporte', es_rif: true },
  { id: null, codigo: 'G', descripcion: 'Ente Gubernamental', es_rif: true },
  { id: null, codigo: 'C', descripcion: 'Comunal', es_rif: true },
  { id: null, codigo: 'V', descripcion: 'Firma personal', es_rif: true },
];

const _etiquetaCat7 = (t) => `${t.codigo} - ${t.descripcion}`;

// Normaliza la respuesta de GET /api/catalogos/tipo-identificacion
// ({ numero, nombre, columnas, valores }) a las opciones del selector.
// Devuelve { opciones, aviso }: `aviso` es null cuando el catálogo llegó
// COMPLETO; si no, trae el texto de la degradación para mostrarlo
// (nada de degradar en silencio — ítem 28-iii).
export const mapearCat7 = (catalogo) => {
  const valores = (catalogo && Array.isArray(catalogo.valores)) ? catalogo.valores : [];
  if (valores.length === 0) {
    return {
      opciones: CAT7_FALLBACK.map((t) => ({ ...t, label: _etiquetaCat7(t) })),
      aviso: 'No se pudo cargar el catálogo de tipos de identificación (Catálogo 7). '
           + 'Se está usando la lista normativa por defecto: la fila elegida NO se '
           + 'guardará como referencia del catálogo. Reintente más tarde.',
    };
  }
  let faltaId = false;
  let faltaEsRif = false;
  const opciones = valores.map((v) => {
    const tieneFlag = (v.es_rif === true || v.es_rif === false);
    if (!tieneFlag) faltaEsRif = true;
    const id = (v.id !== null && v.id !== undefined) ? Number(v.id) : null;
    if (id === null) faltaId = true;
    return {
      id,
      codigo: v.codigo,
      descripcion: v.descripcion,
      es_rif: tieneFlag ? v.es_rif : esRifDerivadoCat7(v.codigo, v.descripcion),
      label: `${v.codigo} - ${v.descripcion}`,
    };
  });
  // (2026-08-05) El catálogo ya NO publica `fuente`: la degradación se detecta
  // por los DATOS que faltan (`id`, `es_rif`), que es una señal derivada del
  // propio payload y no una etiqueta que haya que creerse.
  let aviso = null;
  if (faltaId || faltaEsRif) {
    const partes = [];
    if (faltaId) partes.push('sin `id` (no se podrá persistir el tipo elegido)');
    if (faltaEsRif) partes.push('sin `es_rif` (se dedujo de la letra)');
    aviso = 'El catálogo de tipos de identificación (Catálogo 7) llegó degradado: '
          + partes.join(', ') + '. Avise al administrador.';
  }
  return { opciones, aviso };
};

// Espejo de servicios_compartidos.validar_tipo_identificacion (longitud).
// Devuelve null si es válido, o el mensaje LITERAL exigido por SENIAT/cliente.
export const validarLongitudCat7 = (tipo, numeroParaContar) => {
  if (!tipo) return null;
  const letra = String(tipo.codigo || '').toUpperCase();
  const msg = `El tipo de documento ${tipo.codigo} - ${tipo.descripcion} es inválido`;
  if (letra === 'P') {
    // Pasaporte: alfanumérico libre 1-20 (excepción; no se cuentan dígitos).
    const cuerpo = (numeroParaContar !== null && numeroParaContar !== undefined)
      ? String(numeroParaContar).trim() : '';
    return (cuerpo.length >= 1 && cuerpo.length <= 20) ? null : msg;
  }
  const n = contarDigitosDocumento(numeroParaContar);
  if (letra === 'V') {
    const ok = esFirmaPersonal(tipo.descripcion) ? (n === 9) : (n >= 6 && n <= 8);
    return ok ? null : msg;
  }
  if (letra === 'J' || letra === 'E' || letra === 'G' || letra === 'C') {
    return (n === 9) ? null : msg;
  }
  // Letra fuera del Cat 7 esperado: no se impone longitud (legacy safe).
  return null;
};

// Espejo del bloque (3) de validar_tipo_identificacion_empresa: coherencia de
// la letra en AMBOS sentidos. Devuelve null o el mensaje de error.
export const validarCoherenciaRifCat7 = (tipo, rifValor) => {
  if (!tipo) return null;
  const bruto = rifValor ? String(rifValor).trim().toUpperCase() : '';
  const primera = bruto.charAt(0);
  const esLetra = /^[A-Z]$/.test(primera);
  const codigo = String(tipo.codigo || '').toUpperCase();
  if (tipo.es_rif) {
    if (!esLetra) {
      return `El tipo de identificación '${tipo.codigo} - ${tipo.descripcion}' `
           + `exige que el R.I.F. comience con la letra '${tipo.codigo}'.`;
    }
    if (primera !== codigo) {
      return `El R.I.F. empieza por '${primera}' pero el tipo de identificación `
           + `seleccionado es '${tipo.codigo} - ${tipo.descripcion}'. `
           + 'La letra del R.I.F. debe coincidir con el tipo.';
    }
    return null;
  }
  if (esLetra) {
    return `El tipo de identificación '${tipo.codigo} - ${tipo.descripcion}' `
         + 'no lleva letra: el número de identificación debe ser solo dígitos.';
  }
  return null;
};

// (2026-08-03, ronda 2 / T1 — DECISIÓN DEL USUARIO) Lista VACÍA a propósito.
//
// Histórico: contenía 'C' (Comunal). El backend de clientes normalizaba el RIF
// con `utils.validar_rif`, cuyo patrón era `^[VEJGP]-\d{8}-\d$`, así que ofrecer
// la fila 'C' en el selector era una trampa (400 garantizado), mientras que el
// backend de EMPRESAS sí la aceptaba — asimetría interina.
//
// El usuario decidió que 'C' debe poder usarse en clientes finales Y empresas;
// `utils.validar_rif` ya incluye la letra (utils.py:351), así que el filtro
// pierde su motivo. La constante y `filtrarTiposCliente` SE CONSERVAN como
// punto único de extensión: si alguna vez otra fila del Catálogo 7 dejara de
// ser válida para clientes, se añade AQUÍ y no en cada formulario.
export const CAT7_LETRAS_NO_VALIDAS_CLIENTE = [];

// Filtra del selector de CLIENTES las filas que el backend rechazaría.
// Ofrecerlas es una trampa: el usuario las elige y recibe un 400 opaco.
export const filtrarTiposCliente = (opciones) =>
  (opciones || []).filter(
    (t) => !CAT7_LETRAS_NO_VALIDAS_CLIENTE.includes(String(t.codigo || '').toUpperCase())
  );

// Espejo del formato que el backend exige según es_rif del tipo:
//   es_rif=true                 -> utils.validar_rif => ^[VEJGPC]-\d{8}-\d$
//   es_rif=true y tipo P        -> EXCEPCIÓN PASAPORTE: alfanumérico libre
//                                  1-20, con o sin prefijo 'P-'.
//   es_rif=false                -> se guarda tal cual; la longitud 6-8 la
//                                  impone validar_tipo_identificacion.
//
// EXCEPCIÓN PASAPORTE — espejo EXACTO de client_routes.py:360-368: la fila 'P'
// tiene es_rif=True (ítem 16: solo V-Natural en False) pero un pasaporte es
// alfanumérico y NO cumple el patrón del RIF; el backend lo detecta ANTES de
// llamar a validar_rif y solo normaliza (strip/upper + no vacío). Su longitud
// la valida `validarLongitudCat7` (rama 'P': 1-20). Sin esta excepción el
// formulario construía 'P-AB123456' y su propio validate() lo rechazaba: NINGÚN
// cliente con pasaporte podía crearse.
//
// `opciones.contexto`:
//   'cliente' (por defecto) -> clase [VEJGPC] (espejo de utils.validar_rif,
//                              utils.py:351). (2026-08-03, T1) La 'C' (Comunal)
//                              entró en la clase por decisión del usuario: ya
//                              NO hay asimetría cliente/empresa para esa fila.
//   'empresa'               -> la letra del TIPO elegido: empresa_routes NO usa
//                              validar_rif, valida solo coherencia de letra +
//                              longitud (servicios_compartidos.py:352-429), así
//                              que el contexto empresa sigue siendo MÁS estricto
//                              (exige exactamente la letra del tipo, no
//                              cualquiera de la clase).
// Devuelve null si es válido o el mensaje de error.
export const validarFormatoRifCat7 = (tipo, rifValor, opciones = {}) => {
  const v = rifValor ? String(rifValor).trim().toUpperCase() : '';
  if (!tipo) {
    return validateFormatRif(v) ? null : 'R.I.F. no presenta un formato válido';
  }
  const letra = String(tipo.codigo || '').toUpperCase();
  if (tipo.es_rif) {
    // --- EXCEPCIÓN PASAPORTE ---
    if (letra === 'P') {
      const cuerpo = v.startsWith('P-') ? v.slice(2) : (v.charAt(0) === 'P' ? v.slice(1) : v);
      // (2026-08-02, re-auditoría FE-A) El formato RIF clásico P-12345678-9
      // (cuerpo con guion interno) es válido en ambos backends y en el
      // frontend legacy: se admite además del cuerpo alfanumérico puro.
      if (!/^[A-Z0-9]{1,20}$/.test(cuerpo) && !/^\d{8}-\d$/.test(cuerpo)) {
        return 'El pasaporte debe ser alfanumérico, de 1 a 20 caracteres (ej: P-AB123456).';
      }
      return null;
    }
    // --- Resto de tipos con es_rif=true ---
    const clase = (opciones.contexto === 'empresa') ? letra : 'VEJGPC';
    const patron = new RegExp(`^[${clase}]-\\d{8}-\\d$`);
    return patron.test(v)
      ? null
      : `R.I.F. no presenta un formato válido. Formato esperado: `
        + `${opciones.contexto === 'empresa' ? letra : '[V|E|J|G|P|C]'}-XXXXXXXX-X (ej: J-12345678-9)`;
  }
  return /^\d{6,8}$/.test(v)
    ? null
    : 'El número de identificación debe tener entre 6 y 8 dígitos, sin letra.';
};